import React from "react";
import styled from "@emotion/styled/macro";
import {
    Grid,
    Typography,
    TextField,
    IconButton,
    Theme,
    useTheme,
    useMediaQuery,
    Box,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import FilterListIcon from '@mui/icons-material/FilterList';
import { MIN_SEARCH_LEN , ENTER_KEY } from "../constants/search";
import CircularProgress from "@mui/material/CircularProgress";

const SearchWrapper = styled.div`
    .MuiInputBase-root {
        padding: 0px 5px;
        & > input {
            padding: 10px 0px 10px 5px;
            ::-webkit-input-placeholder {
                font-style: italic;
                font-weight: ${({ theme }: { theme: Theme }) => theme.typography.fontWeightLight};
              }
              ::-moz-placeholder {
                font-style: italic;
                font-weight: ${({ theme }: { theme: Theme }) => theme.typography.fontWeightLight};
              }
              :-ms-input-placeholder {
                font-style: italic;
                font-weight: ${({ theme }: { theme: Theme }) => theme.typography.fontWeightLight};
              }
              :-moz-placeholder {
                  font-style: italic;
                  font-weight: ${({ theme }: { theme: Theme }) => theme.typography.fontWeightLight};
              }
        }
        & > fieldset {
            border-color: ${({ theme }: { theme: Theme }) => theme.palette.text.primary} !important;
            border-width: 1px !important;
        }
    }
`;

interface ISearch {
    text: any;
    clearSearch: any;
    onChange?: any;
    keyUp?: () => void;
    value: string;
    isFullWidth?: boolean;
    showFilterButton?: boolean;
    openFilter?: boolean;
    onFilterClick?: any;
    applySearch?: any;
    showLoaderAdornment?: boolean;
    onOpenFocus?: boolean;
}

const IndividualSearch = (props: ISearch) => {

    const { text } = props;

    const { t }: { t: any } = useTranslation();
    const theme = useTheme();
    const isMobileSize = useMediaQuery(theme.breakpoints.down('sm'));

    if (props.isFullWidth) {
        return (
            <SearchWrapper theme={theme}>
                <TextField
                    variant="outlined"
                    value={props.value}
                    fullWidth={true}
                    onChange={props.onChange}
                    onKeyUp={(e) => {if(e.key !== ENTER_KEY && props.keyUp){props.keyUp!();}}}
                    onClick={
                        () => {
                            if (props.openFilter === true) {
                                props.keyUp!();
                            }
                        }
                    }
                    autoFocus={props.onOpenFocus}
                    placeholder={t("Search by terms")}
                    autoComplete='off'
                    InputProps={{
                        startAdornment: (
                            <Box sx={{
                                position: "relative"
                            }}>
                                <SearchIcon fontSize="small" color="inherit" />
                                {
                                    props.showLoaderAdornment &&
                                    (
                                        <CircularProgress
                                            disableShrink
                                            size={30}
                                            color="secondary"
                                            sx={{
                                                position: "absolute",
                                                left: -3,
                                                top: -3
                                            }}
                                        />
                                    )
                                }
                            </Box>
                        ),
                        endAdornment: (
                            <>
                                {
                                    props.showFilterButton &&
                                    (
                                        <IconButton
                                            title={t("Filter")}
                                            aria-label={t("Filter")}
                                            size="small"
                                            sx={{
                                                backgroundColor: props.openFilter ? theme.palette.text.primary : "none",
                                                color: props.openFilter ? theme.palette.primary.contrastText : "inheritt",
                                            }}
                                            onClick={
                                                () => {
                                                    props.onFilterClick(!props.openFilter);
                                                }
                                            }>
                                            <FilterListIcon fontSize="small" />
                                        </IconButton>
                                    )
                                }
                                <IconButton
                                    color="inherit"
                                    title={t("Clear")}
                                    aria-label={t("Clear")}
                                    size="small"
                                    style={{
                                        visibility: props.value ? 'visible' : 'hidden',
                                        width: props.value ? 'auto' : '0px',
                                    }}
                                    onClick={props.clearSearch}>
                                    <ClearIcon fontSize="small" />
                                </IconButton>
                            </>
                        ),
                    }}
                />
            </SearchWrapper>
        );
    } else {
        return (
            <Grid
                container
                my={isMobileSize ? 5 : 7}
                alignItems={isMobileSize ? 'flex-start' : 'center'}
                flexDirection={isMobileSize ? 'column' : 'row'}
                columnSpacing={isMobileSize ? 0 : 10}
                rowSpacing={isMobileSize ? 3 : 0}>
                <Grid item sx={{ width: isMobileSize ? 'inherit' : 'auto' }}>
                    <Typography
                        component="p"
                        variant="body1"
                        fontWeight={'fontWeightLight'}
                        children={
                            text
                        }
                    />
                </Grid>
                <Grid item sx={{ width: isMobileSize ? 'inherit' : 'auto' }}>
                    <SearchWrapper theme={theme}>
                        <TextField
                            variant="outlined"
                            value={props.value}
                            autoComplete="off"
                            fullWidth={isMobileSize ? true : false}
                            onChange={props.onChange}
                            placeholder={t("Search by terms")}
                            onKeyUp={(e) => {if(e.key !== ENTER_KEY && props.keyUp){props.keyUp!();}}}
                            InputProps={{
                                startAdornment: props.applySearch ?
                                    <IconButton
                                        type="submit"
                                        sx={{ p: '10px' }}
                                        disabled={props.value.length >= MIN_SEARCH_LEN ? false : true}
                                        aria-label={t("Search")}
                                        onClick={() => props.applySearch()}
                                    >
                                        <SearchIcon fontSize="small" color="inherit" />
                                    </IconButton> :
                                    (props.showLoaderAdornment ? (
                                        <Box>
                                        <CircularProgress
                                            disableShrink
                                            size={20}
                                            color="secondary"
                                        />
                                        </Box>
                                    ) : (
                                        <SearchIcon
                                            fontSize="small"
                                            color="inherit"
                                        />)),
                                endAdornment: (
                                    <IconButton
                                        color="inherit"
                                        title={t("Clear")}
                                        aria-label={t("Clear")}
                                        size="small"
                                        style={{ visibility: props.value ? 'visible' : 'hidden' }}
                                        onClick={props.clearSearch}>
                                        <ClearIcon fontSize="small" />
                                    </IconButton>
                                ),
                            }}
                        />
                    </SearchWrapper>
                </Grid>
            </Grid>
        );
    }
};

export default IndividualSearch;